import React from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Chip,
} from "@mui/material";
import DevicesIcon from "@mui/icons-material/Devices";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PeopleIcon from "@mui/icons-material/People";
import InsightsIcon from "@mui/icons-material/Insights";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import HistoryIcon from "@mui/icons-material/History";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import PublicIcon from "@mui/icons-material/Public";
import BuildIcon from "@mui/icons-material/Build";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import CloudDoneIcon from "@mui/icons-material/CloudDone";

const FEATURES = [
  {
    Icon: DevicesIcon,
    color: "#3b82f6",
    bg: "#eff6ff",
    title: "Solutions (Devices)",
    desc: "Track all deployed IoT/GPS devices in the field with full lifecycle visibility.",
  },
  {
    Icon: LocalShippingIcon,
    color: "#10b981",
    bg: "#ecfdf5",
    title: "Vehicles Management",
    desc: "Manage all registered trucks and their installed devices across your fleet.",
  },
  {
    Icon: PeopleIcon,
    color: "#8b5cf6",
    bg: "#f5f3ff",
    title: "Customer Management",
    desc: "Track hauliers and clients with real-time visibility and billing records.",
  },
  {
    Icon: InsightsIcon,
    color: "#f59e0b",
    bg: "#fffbeb",
    title: "Operations Overview",
    desc: "Analytics on system usage, installation status, and journey logs.",
  },
  {
    Icon: AdminPanelSettingsIcon,
    color: "#ef4444",
    bg: "#fef2f2",
    title: "Role-Based Access",
    desc: "Secure and tailored user access control per department and responsibility.",
  },
  {
    Icon: HistoryIcon,
    color: "#0ea5e9",
    bg: "#f0f9ff",
    title: "Device History Logs",
    desc: "View and audit device installation, movement, and transfer history.",
  },
];

const STATS = [
  { label: "Workflow Types", value: "4", sub: "Install · Repair · Removal · Complaint" },
  { label: "Departments", value: "5", sub: "Sales · Tech · Prod · Store · Support" },
  { label: "Export Formats", value: "PDF", sub: "All tables export-ready" },
  { label: "Access Control", value: "RBAC", sub: "Role-based permissions" },
];

const TECH = ["React 19", "MUI v7", "Django REST", "Token Auth", "jsPDF", "Axios"];

const FeatureCard = ({ Icon, color, bg, title, desc }) => (
  <Card
    elevation={0}
    sx={{
      height: "100%",
      border: "1px solid #e2e8f0",
      borderRadius: 2.5,
      transition: "box-shadow 0.2s, transform 0.2s",
      "&:hover": {
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        transform: "translateY(-3px)",
      },
    }}
  >
    <CardContent sx={{ p: 2.5 }}>
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2,
          background: bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1.5,
        }}
      >
        <Icon sx={{ color, fontSize: 22 }} />
      </Box>
      <Typography variant="subtitle2" fontWeight={700} color="#0b1e3d" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.79rem", lineHeight: 1.55 }}>
        {desc}
      </Typography>
    </CardContent>
  </Card>
);

const AboutPage = () => {
  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 5 }}>
      {/* Hero Header */}
      <Box
        sx={{
          borderRadius: 3,
          background: "linear-gradient(135deg, #0b1e3d 0%, #1a4a8a 100%)",
          p: { xs: 3, md: 4 },
          mb: 3,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "radial-gradient(circle at 80% 50%, rgba(96,165,250,0.15) 0%, transparent 55%), radial-gradient(circle at 10% 80%, rgba(59,130,246,0.08) 0%, transparent 40%)",
            pointerEvents: "none",
          }}
        />
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Chip
              label="v1.0 · Monit Onsite CRM"
              size="small"
              sx={{
                mb: 1.5,
                background: "rgba(255,255,255,0.12)",
                color: "rgba(255,255,255,0.85)",
                fontSize: "0.72rem",
                fontWeight: 500,
                border: "1px solid rgba(255,255,255,0.2)",
              }}
            />
            <Typography variant="h5" fontWeight={800} color="#fff" gutterBottom>
              Monit Onsite CRM
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.7)", maxWidth: 560, lineHeight: 1.7 }}
            >
              A tailored field-operations platform built to manage and streamline asset-intensive
              industries — logistics, construction, and fleet management — from a single secure
              dashboard.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
              {TECH.map(t => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  sx={{
                    background: "rgba(255,255,255,0.1)",
                    color: "rgba(255,255,255,0.8)",
                    fontSize: "0.7rem",
                    border: "1px solid rgba(255,255,255,0.15)",
                  }}
                />
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={1.5}>
              {STATS.map(({ label, value, sub }) => (
                <Grid item xs={6} key={label}>
                  <Box
                    sx={{
                      background: "rgba(255,255,255,0.08)",
                      border: "1px solid rgba(255,255,255,0.12)",
                      borderRadius: 2,
                      p: 1.5,
                      textAlign: "center",
                    }}
                  >
                    <Typography variant="h6" fontWeight={800} color="#60a5fa" sx={{ lineHeight: 1 }}>
                      {value}
                    </Typography>
                    <Typography variant="caption" fontWeight={600} color="rgba(255,255,255,0.8)" display="block" sx={{ mt: 0.3 }}>
                      {label}
                    </Typography>
                    <Typography variant="caption" color="rgba(255,255,255,0.45)" sx={{ fontSize: "0.65rem" }}>
                      {sub}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Feature Cards */}
      <Typography variant="subtitle2" fontWeight={700} color="#0b1e3d" sx={{ mb: 1.5, textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.72rem" }}>
        Core Modules
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {FEATURES.map(feat => (
          <Grid item xs={12} sm={6} md={4} key={feat.title}>
            <FeatureCard {...feat} />
          </Grid>
        ))}
      </Grid>

      {/* Mission + Vision + Pillars */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2.5, height: "100%" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <TrackChangesIcon sx={{ color: "#3b82f6", fontSize: 18 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight={700} color="#0b1e3d">
                  Our Mission
                </Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                To <strong>simplify, digitize, and optimize</strong> the way Monit manages its
                solutions, vehicles, and customers — all from a centralized and secure dashboard.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2.5, height: "100%" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: "#f5f3ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <PublicIcon sx={{ color: "#8b5cf6", fontSize: 18 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight={700} color="#0b1e3d">
                  Our Vision
                </Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                To become the backbone of Monit's field intelligence system — where every asset,
                trip, and customer interaction is recorded and optimized for growth.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2.5, height: "100%" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Box sx={{ width: 32, height: 32, borderRadius: 1.5, background: "#ecfdf5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <BuildIcon sx={{ color: "#10b981", fontSize: 18 }} />
                </Box>
                <Typography variant="subtitle2" fontWeight={700} color="#0b1e3d">
                  Design Pillars
                </Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {[
                  { Icon: SecurityIcon, color: "#ef4444", text: "Secure by default — token-based auth" },
                  { Icon: SpeedIcon, color: "#f59e0b", text: "Fast, responsive across all devices" },
                  { Icon: CloudDoneIcon, color: "#3b82f6", text: "Real-time sync with backend API" },
                ].map(({ Icon, color, text }) => (
                  <Box key={text} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon sx={{ fontSize: 15, color }} />
                    <Typography variant="caption" color="text.secondary">{text}</Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AboutPage;
